import React from 'react'
import { shape, func, string, bool } from 'prop-types'
import classNames from 'classnames'

import { SuggestContentES_v3 as SuggestContentES } from 'views/utils/propTypes'
import { isRequired } from 'views/utils/propTypeUtils'
import Image from 'views/components/atoms/Image'
import {
  currencyToSymbol,
  formatPriceString
} from 'views/components/atoms/Price/helpers'
import { getPriceForType } from 'shared/utils/productTemplateUtils'
import { PRICE_TYPES } from 'shared/consts/productTemplates'
import store from 'shared/store'

import './SearchBoxListItem.scss'

/**
 * enriches the normal product name with a bold mark for the (first occurence) of the query string
 * we use slice to cut and inject the bold mark, as this enables us to retain the original case
 * of the search query
 * also all chars exceeding max amount of 100 (45 on mobile) will be replaced with ...
 *
 * @param  {Object} args
 * @param  {string} args.name      normal product name
 * @param  {string} args.queryStr  query string searched for
 * @param  {bool} args.concat    flag if rendered for concat name
 * @returns {JSX.Element}          rich product name
 */
export const renderName = ({ name, queryStr, concat }) => {
  let displayName = name

  if (concat) {
    displayName = displayName.substring(0, 44).concat('…')
  } else {
    displayName = displayName.substring(0, 99)
  }

  const startPos = displayName.toLowerCase().indexOf(queryStr)
  if (startPos > -1) {
    const endPos = startPos + queryStr.length

    const prefix = displayName.slice(0, startPos)
    const needle = displayName.slice(startPos, endPos)
    const suffix = displayName.slice(endPos)

    return (
      <span className="u-color--blue u-font-weight--bold">
        {prefix}
        <span className="u-color--dark u-font-weight--normal">{needle}</span>
        {suffix}
      </span>
    )
  }

  return <span>{displayName}</span>
}

renderName.propTypes = {
  name: string.isRequired,
  queryStr: string.isRequired,
  concat: bool
}

renderName.defaultProps = {
  concat: false
}

const renderPrice = (price, locale) => {
  const { amount } = price
  const currencySymbol = currencyToSymbol('EUR')
  return formatPriceString({
    amount,
    locale,
    currencySymbol
  })
}

// eslint-disable-next-line react/prop-types
const renderPrices = ({ prices, locale }) => {
  const listPrice = getPriceForType(prices, PRICE_TYPES.LIST)
  const retailPrice = getPriceForType(prices, PRICE_TYPES.RETAIL)

  return (
    <>
      {listPrice && listPrice.amount > retailPrice.amount && (
        <span className="o-SearchBox__price--list u-margin--right">
          {renderPrice(listPrice, locale)}
        </span>
      )}
      <span>{renderPrice(retailPrice, locale)}</span>
    </>
  )
}

const SearchBoxListItem = ({
  mobile,
  item: { id, name, prices, deeplink, thumbnailURL },
  queryStr,
  handleItemClick,
  highlighted,
  last
}) => {
  const classList = classNames(
    'a-link a-link--no-underline o-SearchBox__item l-flex ',
    {
      'o-SearchBox__item--highlighted': highlighted,
      'u-border--bottom@medium': !last
    }
  )

  const {
    publicConfig: { locale }
  } = store.getPublicRuntimeConfig()

  const productUrl = deeplink.indexOf('/', 0) === 0 ? deeplink : `/${deeplink}`

  return (
    <li key={`product-${id}`} data-product-id={id}>
      <a
        className={classList}
        href={productUrl}
        onClick={() => handleItemClick?.()}
        data-clientside-hook="SearchBoxListItemLink"
      >
        <Image
          src={thumbnailURL}
          srcWidth={128}
          srcHeight={128}
          className="o-SearchBox__image u-border-radius u-background-color--background u-margin--right l-flex__fixed"
          alt={name}
        />
        <span className="o-SearchBox__label l-flex__primary">
          {renderName({ name, queryStr, concat: mobile })}
        </span>
        <span className="u-margin--left l-flex__fixed">
          <div className="o-SearchBox__price a-h2 u-no-margin u-text-align--right u-text-align--left@small-tiny u-font-weight--normal u-no-wrap u-color--blue">
            {renderPrices({ prices, locale })}
          </div>
        </span>
      </a>
    </li>
  )
}

SearchBoxListItem.propTypes = {
  mobile: bool,
  item: shape(isRequired(SuggestContentES)).isRequired,
  queryStr: string,
  handleItemClick: func,
  highlighted: bool,
  last: bool
}

SearchBoxListItem.defaultProps = {
  mobile: null,
  queryStr: null,
  handleItemClick: null,
  highlighted: false,
  last: true
}

export default SearchBoxListItem
